import type { WorkspaceKey } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { RawTextInput } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Tree, TreeButtons, useOpenPath } from '@festina-life/ui/listsAndTables'
import { Icon } from '@festina-life/ui/structure'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'

import { useAllConciseBranches } from '../../../uiModels/branches/useAllConciseBranches'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { WorkspaceName } from '../../workspaceName/WorkspaceName'
import { useWorkspacesTree } from '../useWorkspacesTree'

export const WORKSPACES_TREE = 'workspacesTree'

type Props = {
  activeWorkspaceKey: Optional<WorkspaceKey>
  onSelect: (nextWorkspace: WorkspaceKey) => void
}

export const WorkspacesTree = ({ onSelect, activeWorkspaceKey }: Props) => {
  const translate = useTranslate()
  const treeContainerRef = useRef<HTMLDivElement>(null)
  const [workspaceQuery, setWorkspaceQuery] = useState('')
  const conciseBranches = useAllConciseBranches()
  const tree = useWorkspacesTree({ query: workspaceQuery, activeWorkspaceKey })
  const openPath = useOpenPath(WORKSPACES_TREE)
  const didExpandPath = useRef(false)
  const { workspace, workspaceKey } = useOperationsContext()

  useEffect(() => {
    if (workspaceKey && workspace && !didExpandPath.current) {
      didExpandPath.current = true
      openPath(workspaceKey.branchKey.server)
    }
  }, [conciseBranches, openPath, workspace, workspaceKey])

  const handleWorkspaceQueryChanged = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setWorkspaceQuery(event.target.value)
  }

  return (
    <div className="p-1 size-full">
      <div className="flex items-center mb-1 gap-x-2">
        <RawTextInput
          value={workspaceQuery}
          onChange={handleWorkspaceQueryChanged}
          addOnProps={{ element: <Icon name="Filter" /> }}
        />
        <TreeButtons nodes={tree} id={WORKSPACES_TREE} />
      </div>

      {tree.length ? (
        <div className="size-full overflow-auto pb-12" ref={treeContainerRef}>
          <Tree
            nodeClassName="py-1"
            leafClassName="py-1 w-full"
            nodes={tree}
            query={workspaceQuery}
            renderLeaf={(leaf) => (
              <WorkspaceName isShowingIcon workspaceKey={leaf.key} />
            )}
            renderNode={(node) => node.name}
            containerRef={treeContainerRef.current}
            id={WORKSPACES_TREE}
            onSelectLeaf={(leaf) => onSelect(leaf.key)}
          />
        </div>
      ) : (
        translate('workspacesPanel.noWorkspaces')
      )}
    </div>
  )
}
